import React from 'react';
import {
  useGridApiContext,
  Toolbar as DataGridToolbar
} from '@mui/x-data-grid';
import { Box, Button, Tooltip, TextField, InputAdornment } from '@mui/material';
import Icons from '../../../components/icons';

interface CustomDataGridToolbarProps {
  showQuickFilter?: boolean;
  disableToolbarButton?: boolean;
}

const CustomDataGridToolbar = ({
  showQuickFilter = true,
  disableToolbarButton = false
}: CustomDataGridToolbarProps) => {
  const apiRef = useGridApiContext();

  const handleOpenColumns = () => {
    // @ts-ignore - Grid v6 API: showPreferences('columns')
    apiRef.current?.showPreferences?.('columns');
  };

  const handleOpenFilters = () => {
    // @ts-ignore - Grid v6 API: showPreferences('filters')
    apiRef.current?.showPreferences?.('filters');
  };

  const handleExportCsv = () => {
    apiRef.current?.exportDataAsCsv?.();
  };

  const [showSearch, setShowSearch] = React.useState(false);
  const [searchValue, setSearchValue] = React.useState('');
  const searchInputRef = React.useRef<HTMLInputElement | null>(null);
  const searchContainerRef = React.useRef<HTMLDivElement | null>(null);

  const applyQuickFilter = (val: string) => {
    // @ts-ignore
    if (apiRef.current?.setQuickFilterValues) {
      // @ts-ignore
      apiRef.current.setQuickFilterValues(val ? [val] : []);
    } else {
      // @ts-ignore
      apiRef.current?.setQuickFilter?.(val);
    }
  };

  const handleSearchIconClick = () => {
    setShowSearch(true);
    setTimeout(() => searchInputRef.current?.focus(), 0);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const val = event.target.value;
    setSearchValue(val);
    applyQuickFilter(val);
  };

  React.useEffect(() => {
    if (!showSearch) return;
    const onDocMouseDown = (e: MouseEvent) => {
      const target = e.target as Node | null;
      if (
        searchContainerRef.current &&
        target &&
        !searchContainerRef.current.contains(target)
      ) {
        setShowSearch(false);
      }
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [showSearch]);

  return (
    <DataGridToolbar>
      <Box
        sx={{
          display: 'flex',
          gap: 1,
          p: 1,
          flexWrap: 'wrap',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title='Columns'>
            <span>
              <Button
                variant='text'
                color='inherit'
                startIcon={<Icons.ColumnsBlack />}
                onClick={handleOpenColumns}
                sx={{ minWidth: 0, p: 0.5 }}
              />
            </span>
          </Tooltip>

          <Tooltip title='Filter'>
            <span>
              <Button
                variant='text'
                color='inherit'
                startIcon={<Icons.FilterBlack />}
                onClick={handleOpenFilters}
                sx={{ minWidth: 0, p: 0.5 }}
              />
            </span>
          </Tooltip>

          {!disableToolbarButton && (
            <Tooltip title='Export CSV'>
              <span>
                <Button
                  variant='text'
                  color='inherit'
                  startIcon={<Icons.ImportBlack />}
                  onClick={handleExportCsv}
                  sx={{ minWidth: 0, p: 0.5 }}
                />
              </span>
            </Tooltip>
          )}
        </Box>

        {showQuickFilter && (
          <Box
            ref={searchContainerRef}
            sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
          >
            {!showSearch ? (
              <Tooltip title='Search'>
                <span>
                  <Button
                    variant='text'
                    color='inherit'
                    startIcon={<Icons.SearchBlack />}
                    onClick={handleSearchIconClick}
                    sx={{ minWidth: 0, p: 0.5 }}
                  />
                </span>
              </Tooltip>
            ) : (
              <Box
                sx={{
                  width: showSearch ? 280 : 0,
                  opacity: showSearch ? 1 : 0,
                  transition:
                    'width 0.5s ease-in-out, opacity 0.4s ease-in-out',
                  overflow: 'hidden'
                }}
              >
                <TextField
                  inputRef={searchInputRef}
                  value={searchValue}
                  onChange={handleSearchChange}
                  placeholder='Search'
                  variant='outlined'
                  size='small'
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position='start'>
                        <Icons.SearchBlack />
                      </InputAdornment>
                    )
                  }}
                />
              </Box>
            )}
          </Box>
        )}
      </Box>
    </DataGridToolbar>
  );
};

export default CustomDataGridToolbar;
